<script>
    import { onMount } from 'svelte';
    import { knowledgeBaseAPI, api } from './api.js';

    let entries = [];
    let filteredEntries = [];
    let categories = [];
    let loading = false;
    let error = null;

    // Filters
    let searchQuery = '';
    let selectedCategory = '';

    // Modal state
    let showModal = false;
    let editingEntry = null;
    let formData = {
        title: '',
        content: '',
        category: '',
        tags: '',
        keywords: '',
        entry_type: 'text',
        priority: 0,
        attachments: [],
        is_active: true
    };

    // Keywords management
    let keywordInput = '';
    let keywordsList = [];

    // File upload state
    let uploadingFile = false;

    onMount(() => {
        loadEntries();
    });

    async function loadEntries() {
        loading = true;
        error = null;
        try {
            const params = {};
            if (selectedCategory) params.category = selectedCategory;
            if (searchQuery) params.search = searchQuery;

            const data = await knowledgeBaseAPI.getAll(params);
            entries = data;
            filteredEntries = data;

            // Extract unique categories
            categories = [...new Set(entries.map(e => e.category).filter(c => c))];
        } catch (err) {
            error = err.message || 'Error al cargar la base de conocimiento';
            console.error('Error loading knowledge base:', err);
        } finally {
            loading = false;
        }
    }

    function filterEntries() {
        filteredEntries = entries.filter(entry => {
            const matchesSearch = !searchQuery ||
                entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                entry.content.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesCategory = !selectedCategory || entry.category === selectedCategory;

            return matchesSearch && matchesCategory;
        });
    }

    function openCreateModal() {
        editingEntry = null;
        formData = {
            title: '',
            content: '',
            category: '',
            tags: '',
            keywords: '',
            entry_type: 'text',
            priority: 0,
            attachments: [],
            is_active: true
        };
        keywordsList = [];
        keywordInput = '';
        showModal = true;
    }

    function openEditModal(entry) {
        editingEntry = entry;
        formData = {
            title: entry.title,
            content: entry.content,
            category: entry.category || '',
            tags: Array.isArray(entry.tags) ? entry.tags.join(', ') : '',
            keywords: '',
            entry_type: entry.entry_type || 'text',
            priority: entry.priority || 0,
            attachments: entry.attachments || [],
            is_active: entry.is_active
        };
        keywordsList = entry.keywords || [];
        keywordInput = '';
        showModal = true;
    }

    function closeModal() {
        showModal = false;
        editingEntry = null;
    }

    function addKeyword() {
        const keyword = keywordInput.trim();
        if (keyword && !keywordsList.includes(keyword)) {
            keywordsList = [...keywordsList, keyword];
            keywordInput = '';
        }
    }

    function removeKeyword(keyword) {
        keywordsList = keywordsList.filter(k => k !== keyword);
    }

    function handleKeywordKeydown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            addKeyword();
        }
    }

    async function handleFileUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        uploadingFile = true;
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch('/api/upload', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            if (!response.ok) throw new Error('Upload failed');

            const data = await response.json();
            formData.attachments = [...formData.attachments, {
                url: data.url,
                type: data.type,
                size: data.size,
                original_name: file.name
            }];

            e.target.value = ''; // Reset file input
        } catch (err) {
            alert('Error al subir archivo: ' + err.message);
        } finally {
            uploadingFile = false;
        }
    }

    function removeAttachment(index) {
        formData.attachments = formData.attachments.filter((_, i) => i !== index);
    }

    function formatFileSize(bytes) {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    async function saveEntry() {
        if (!formData.title || !formData.content) {
            alert('Por favor complete título y contenido');
            return;
        }

        loading = true;
        error = null;

        try {
            const entryData = {
                title: formData.title,
                content: formData.content,
                category: formData.category || null,
                tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
                keywords: keywordsList.length > 0 ? keywordsList : null,
                entry_type: formData.entry_type,
                priority: parseInt(formData.priority) || 0,
                attachments: formData.attachments.length > 0 ? formData.attachments : null,
                is_active: formData.is_active
            };

            if (editingEntry) {
                await knowledgeBaseAPI.update(editingEntry.id, entryData);
            } else {
                await knowledgeBaseAPI.create(entryData);
            }

            closeModal();
            await loadEntries();
        } catch (err) {
            error = err.message || 'Error al guardar la entrada';
            console.error('Error saving entry:', err);
        } finally {
            loading = false;
        }
    }

    async function deleteEntry(id) {
        if (!confirm('¿Está seguro de eliminar esta entrada?')) return;

        loading = true;
        error = null;

        try {
            await knowledgeBaseAPI.delete(id);
            await loadEntries();
        } catch (err) {
            error = err.message || 'Error al eliminar la entrada';
            console.error('Error deleting entry:', err);
        } finally {
            loading = false;
        }
    }

    function getCategoryColor(category) {
        const colors = {
            'General': '#2196f3',
            'Soporte': '#4caf50',
            'Ventas': '#ff9800',
            'Pagos': '#9c27b0',
            'Productos': '#f44336',
            'Servicios': '#00bcd4'
        };
        return colors[category] || '#757575';
    }

    $: if (searchQuery !== '' || selectedCategory !== '') {
        filterEntries();
    }
</script>

<style>
    .knowledge-container {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .header h1 {
        margin: 0;
        color: #333;
    }

    .filters {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .search-box {
        flex: 1;
        min-width: 250px;
        padding: 10px 15px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
    }

    .filter-select {
        padding: 10px 15px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        background: white;
        cursor: pointer;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .btn-primary:hover {
        background: #1976d2;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-secondary:hover {
        background: #616161;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    .btn-success:hover {
        background: #388e3c;
    }

    .btn-danger {
        background: #f44336;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-danger:hover {
        background: #d32f2f;
    }

    .btn-edit {
        background: #ff9800;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-edit:hover {
        background: #f57c00;
    }

    .entries-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .entry-card {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.2s;
        position: relative;
    }

    .entry-card:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        transform: translateY(-2px);
    }

    .entry-card.inactive {
        opacity: 0.6;
        background: #f5f5f5;
    }

    .entry-header {
        margin-bottom: 15px;
    }

    .entry-title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin: 0 0 8px 0;
    }

    .entry-meta {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        margin-bottom: 10px;
    }

    .category-badge {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        color: white;
        font-weight: 500;
    }

    .tag {
        display: inline-block;
        padding: 4px 10px;
        background: #e3f2fd;
        color: #1976d2;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 500;
    }

    .entry-content {
        color: #666;
        font-size: 14px;
        line-height: 1.6;
        margin-bottom: 15px;
        max-height: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .entry-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
    }

    .status-indicator {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #4caf50;
    }

    .status-indicator.inactive {
        background: #757575;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 8px;
        padding: 30px;
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal h2 {
        margin: 0 0 20px 0;
        color: #333;
    }

    .form-group {
        margin-bottom: 20px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
        font-size: 14px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .form-group textarea {
        min-height: 150px;
        resize: vertical;
    }

    .form-group small {
        display: block;
        margin-top: 5px;
        color: #757575;
        font-size: 12px;
    }

    .checkbox-group {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .checkbox-group input[type="checkbox"] {
        width: auto;
        cursor: pointer;
    }

    .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 25px;
    }

    .error-message {
        background: #ffebee;
        color: #c62828;
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 20px;
        border-left: 4px solid #c62828;
    }

    .loading {
        text-align: center;
        padding: 40px;
        color: #757575;
    }

    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #757575;
    }

    .empty-state h3 {
        color: #333;
        margin-bottom: 10px;
    }

    .stats {
        display: flex;
        gap: 20px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .stat-card {
        background: white;
        padding: 15px 20px;
        border-radius: 8px;
        border: 1px solid #e0e0e0;
        flex: 1;
        min-width: 150px;
    }

    .stat-value {
        font-size: 28px;
        font-weight: 700;
        color: #2196f3;
        margin-bottom: 5px;
    }

    .stat-label {
        font-size: 13px;
        color: #757575;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    /* Keywords/Triggers */
    .keyword-chip {
        display: inline-block;
        padding: 4px 8px;
        background: #fff3e0;
        color: #f57c00;
        border: 1px solid #ffb74d;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        margin-right: 4px;
        margin-bottom: 4px;
    }

    .keyword-input-container {
        display: flex;
        gap: 8px;
        margin-bottom: 10px;
    }

    .keyword-input {
        flex: 1;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 13px;
    }

    .btn-add-keyword {
        padding: 8px 16px;
        background: #4caf50;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
    }

    .keywords-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 8px;
        min-height: 30px;
    }

    .keyword-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 5px 10px;
        background: #fff3e0;
        color: #f57c00;
        border: 1px solid #ffb74d;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
    }

    .keyword-remove {
        cursor: pointer;
        font-weight: bold;
        color: #f57c00;
        opacity: 0.7;
    }

    .keyword-remove:hover {
        opacity: 1;
    }

    /* Attachments */
    .attachment-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px;
        background: #f5f5f5;
        border-radius: 4px;
        margin-top: 8px;
    }

    .attachment-icon {
        font-size: 20px;
    }

    .attachment-info {
        flex: 1;
    }

    .attachment-name {
        font-size: 13px;
        color: #333;
        font-weight: 500;
    }

    .attachment-size {
        font-size: 11px;
        color: #757575;
    }

    .btn-remove-attachment {
        padding: 4px 8px;
        background: #f44336;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 11px;
    }

    .file-upload-area {
        margin-top: 10px;
    }

    .file-upload-label {
        display: inline-block;
        padding: 10px 16px;
        background: #2196f3;
        color: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
        transition: background 0.2s;
    }

    .file-upload-label:hover {
        background: #1976d2;
    }

    .file-upload-label.uploading {
        background: #757575;
        cursor: not-allowed;
    }

    /* Entry Type Badge */
    .entry-type-badge {
        display: inline-block;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .entry-type-badge.text {
        background: #e3f2fd;
        color: #1976d2;
    }

    .entry-type-badge.faq {
        background: #f3e5f5;
        color: #7b1fa2;
    }

    .entry-type-badge.trigger {
        background: #fff3e0;
        color: #f57c00;
    }

    /* Priority Indicator */
    .priority-indicator {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        background: #ffebee;
        color: #c62828;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
    }

    .priority-indicator.high {
        background: #ffebee;
        color: #c62828;
    }

    .priority-indicator.medium {
        background: #fff3e0;
        color: #f57c00;
    }

    .priority-indicator.low {
        background: #e8f5e9;
        color: #2e7d32;
    }

    /* Entry indicators row */
    .entry-indicators {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        margin-bottom: 10px;
    }
</style>

<div class="knowledge-container">
    <div class="header">
        <h1>📚 Base de Conocimiento</h1>
        <button class="btn btn-primary" on:click={openCreateModal}>
            + Nueva Entrada
        </button>
    </div>

    {#if error}
        <div class="error-message">{error}</div>
    {/if}

    <div class="stats">
        <div class="stat-card">
            <div class="stat-value">{entries.length}</div>
            <div class="stat-label">Entradas Totales</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">{entries.filter(e => e.is_active).length}</div>
            <div class="stat-label">Activas</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">{categories.length}</div>
            <div class="stat-label">Categorías</div>
        </div>
    </div>

    <div class="filters">
        <input
            type="text"
            class="search-box"
            placeholder="🔍 Buscar por título o contenido..."
            bind:value={searchQuery}
        />

        <select class="filter-select" bind:value={selectedCategory} on:change={loadEntries}>
            <option value="">Todas las categorías</option>
            {#each categories as category}
                <option value={category}>{category}</option>
            {/each}
        </select>

        {#if searchQuery || selectedCategory}
            <button class="btn btn-secondary" on:click={() => {
                searchQuery = '';
                selectedCategory = '';
                loadEntries();
            }}>
                Limpiar Filtros
            </button>
        {/if}
    </div>

    {#if loading}
        <div class="loading">Cargando...</div>
    {:else if filteredEntries.length === 0}
        <div class="empty-state">
            <h3>No hay entradas</h3>
            <p>Crea tu primera entrada de conocimiento para que el chatbot pueda usarla.</p>
        </div>
    {:else}
        <div class="entries-grid">
            {#each filteredEntries as entry}
                <div class="entry-card" class:inactive={!entry.is_active}>
                    <div class="status-indicator" class:inactive={!entry.is_active}></div>

                    <div class="entry-header">
                        <h3 class="entry-title">{entry.title}</h3>

                        <div class="entry-indicators">
                            <span class="entry-type-badge {entry.entry_type || 'text'}">
                                {entry.entry_type || 'text'}
                            </span>

                            {#if entry.priority && entry.priority > 0}
                                <span class="priority-indicator {entry.priority >= 5 ? 'high' : entry.priority >= 2 ? 'medium' : 'low'}">
                                    ⭐ {entry.priority}
                                </span>
                            {/if}

                            {#if entry.keywords && entry.keywords.length > 0}
                                <span class="keyword-chip">🔑 {entry.keywords.length} triggers</span>
                            {/if}

                            {#if entry.attachments && entry.attachments.length > 0}
                                <span class="keyword-chip">📎 {entry.attachments.length} archivos</span>
                            {/if}
                        </div>

                        <div class="entry-meta">
                            {#if entry.category}
                                <span
                                    class="category-badge"
                                    style="background-color: {getCategoryColor(entry.category)}"
                                >
                                    {entry.category}
                                </span>
                            {/if}

                            {#if entry.tags && entry.tags.length > 0}
                                {#each entry.tags.slice(0, 3) as tag}
                                    <span class="tag">#{tag}</span>
                                {/each}
                                {#if entry.tags.length > 3}
                                    <span class="tag">+{entry.tags.length - 3}</span>
                                {/if}
                            {/if}
                        </div>
                    </div>

                    <div class="entry-content">
                        {entry.content}
                    </div>

                    <div class="entry-actions">
                        <button class="btn btn-edit" on:click={() => openEditModal(entry)}>
                            ✏️ Editar
                        </button>
                        <button class="btn btn-danger" on:click={() => deleteEntry(entry.id)}>
                            🗑️ Eliminar
                        </button>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

{#if showModal}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal" on:click|stopPropagation>
            <h2>{editingEntry ? 'Editar Entrada' : 'Nueva Entrada'}</h2>

            <form on:submit|preventDefault={saveEntry}>
                <div class="form-group">
                    <label for="title">Título *</label>
                    <input
                        id="title"
                        type="text"
                        bind:value={formData.title}
                        placeholder="Ej: ¿Qué es nuestro servicio?"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="content">Contenido *</label>
                    <textarea
                        id="content"
                        bind:value={formData.content}
                        placeholder="Escribe la información que el chatbot debe conocer..."
                        required
                    ></textarea>
                    <small>El chatbot usará esta información para responder preguntas.</small>
                </div>

                <div class="form-group">
                    <label for="category">Categoría</label>
                    <input
                        id="category"
                        type="text"
                        bind:value={formData.category}
                        placeholder="Ej: General, Soporte, Ventas, Pagos"
                        list="category-suggestions"
                    />
                    <datalist id="category-suggestions">
                        {#each categories as category}
                            <option value={category}></option>
                        {/each}
                        <option value="General"></option>
                        <option value="Soporte"></option>
                        <option value="Ventas"></option>
                        <option value="Pagos"></option>
                        <option value="Productos"></option>
                        <option value="Servicios"></option>
                    </datalist>
                    <small>Organiza las entradas por categorías.</small>
                </div>

                <div class="form-group">
                    <label for="tags">Etiquetas</label>
                    <input
                        id="tags"
                        type="text"
                        bind:value={formData.tags}
                        placeholder="crm, introducción, ayuda"
                    />
                    <small>Separa las etiquetas con comas.</small>
                </div>

                <div class="form-group">
                    <label for="entry_type">Tipo de Entrada</label>
                    <select id="entry_type" bind:value={formData.entry_type}>
                        <option value="text">Text - Información general</option>
                        <option value="faq">FAQ - Pregunta frecuente</option>
                        <option value="trigger">Trigger - Respuesta automática por palabra clave</option>
                    </select>
                    <small>El tipo 'trigger' responde automáticamente cuando el usuario menciona las palabras clave.</small>
                </div>

                <div class="form-group">
                    <label>Palabras Clave / Triggers</label>
                    <div class="keyword-input-container">
                        <input
                            type="text"
                            class="keyword-input"
                            bind:value={keywordInput}
                            on:keydown={handleKeywordKeydown}
                            placeholder="Ej: precio, costo, cuanto cuesta"
                        />
                        <button
                            type="button"
                            class="btn-add-keyword"
                            on:click={addKeyword}
                        >
                            + Agregar
                        </button>
                    </div>
                    {#if keywordsList.length > 0}
                        <div class="keywords-list">
                            {#each keywordsList as keyword}
                                <span class="keyword-item">
                                    {keyword}
                                    <span class="keyword-remove" on:click={() => removeKeyword(keyword)}>×</span>
                                </span>
                            {/each}
                        </div>
                    {:else}
                        <small>Cuando el usuario mencione estas palabras, esta entrada tendrá prioridad.</small>
                    {/if}
                </div>

                <div class="form-group">
                    <label for="priority">Prioridad</label>
                    <input
                        id="priority"
                        type="number"
                        min="0"
                        max="10"
                        bind:value={formData.priority}
                    />
                    <small>0-10. Mayor prioridad = la IA la considerará primero (0 = normal, 5+ = alta prioridad)</small>
                </div>

                <div class="form-group">
                    <label>Archivos Adjuntos</label>
                    <div class="file-upload-area">
                        <label class="file-upload-label" class:uploading={uploadingFile}>
                            {uploadingFile ? '⏳ Subiendo...' : '📎 Subir Archivo (PDF, Imagen, Video)'}
                            <input
                                type="file"
                                style="display: none;"
                                on:change={handleFileUpload}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi"
                                disabled={uploadingFile}
                            />
                        </label>
                    </div>
                    {#if formData.attachments && formData.attachments.length > 0}
                        {#each formData.attachments as attachment, index}
                            <div class="attachment-item">
                                <span class="attachment-icon">
                                    {#if attachment.type === 'image'}🖼️
                                    {:else if attachment.type === 'video'}🎥
                                    {:else if attachment.type === 'document'}📄
                                    {:else}📎
                                    {/if}
                                </span>
                                <div class="attachment-info">
                                    <div class="attachment-name">{attachment.original_name}</div>
                                    <div class="attachment-size">{formatFileSize(attachment.size)}</div>
                                </div>
                                <button
                                    type="button"
                                    class="btn-remove-attachment"
                                    on:click={() => removeAttachment(index)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        {/each}
                    {:else}
                        <small>PDFs, imágenes y videos que complementan esta entrada.</small>
                    {/if}
                </div>

                <div class="form-group">
                    <div class="checkbox-group">
                        <input
                            id="is_active"
                            type="checkbox"
                            bind:checked={formData.is_active}
                        />
                        <label for="is_active">Entrada activa (visible para el chatbot)</label>
                    </div>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" on:click={closeModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-success" disabled={loading}>
                        {loading ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}
